import React, {
  useEffect, useReducer, useCallback,
} from 'react';
import type Locale from 'date-fns';

import QuestionDisplay from './QuestionDisplay';
import Styled from './AssessmentForm.styles';
import { ActionTypeEnum, reducer } from './AssessmentForm.reducer';

import Text from '../Text';
import Box from '../Box';

import {
  Question,
  Assessment,
  AssessmentResult,
} from '../../types/DataTypes';

import { getAssessmentAnswers, getInitialAssessmentFormState } from '../../utils/assessmentUtils';
import { AssessmentResultAnswer } from '../../types';

type Props<T> = {
    assessment: Assessment;
    onSubmit?: (assessmentAnswers: string[], changeReason?: string) => Promise<T>;
    onCancel?: () => void;
    onStart?: () => void;
    onSubmitWithSkippedQuestions: (skippedQuestions: Question[], onSubmit: () => void) => void;
    onSubmitWithChangeReason?: (onSubmit: () => Promise<T>) => void;
    submitButtonText?: string;
    cancelButtonText?: string;
    fieldRequiredText?: string;
    canSkipQuestions?: boolean;
    assessmentResult?: AssessmentResult;
    disabled?: boolean;
    locale?: Locale;
};
const AssessmentForm = <T, >(props: Props<T>) => {
  const {
    assessment,
    canSkipQuestions,
    onSubmit,
    onSubmitWithSkippedQuestions,
    onSubmitWithChangeReason,
    assessmentResult,
    onCancel,
    onStart,
    submitButtonText,
    cancelButtonText,
    fieldRequiredText,
    disabled,
    locale,
  } = props;
  const [state, dispatch] = useReducer(
    reducer, getInitialAssessmentFormState(assessment.questions, assessmentResult),
  );

  // This only happens once, after the initial render.
  useEffect(() => {
    if (onStart) {
      onStart();
    }
  }, []);

  const submit = (changeReason?: string) => {
    const assessmentAnswers = getAssessmentAnswers(state);
    return onSubmit && onSubmit(assessmentAnswers, changeReason);
  };

  const submitOrUpdate = () => {
    if (assessmentResult) {
      onSubmitWithChangeReason(submit);
    } else {
      submit();
    }
  };

  const handleSkippedQuestions = (skippedQuestions: Question[]) => {
    if (skippedQuestions.length > 0) {
      if (canSkipQuestions) {
        onSubmitWithSkippedQuestions(
          skippedQuestions,
          () => {
            dispatch({ type: ActionTypeEnum.CLEAR_ERROR_STATES });
            submitOrUpdate();
          },
        );
      } else {
        // Scroll to first unanswered required question.
        document.getElementById(`question-${skippedQuestions[0].ordinal}`)?.scrollIntoView();
      }
    }
  };

  const handleSubmit = () => {
    dispatch({
      type: ActionTypeEnum.CHECK_REQUIRED_QUESTIONS,
    });

    const questionsInErrorState = assessment.questions.filter(
      (question) => state[question.ordinal].inErrorState,
    );

    if (questionsInErrorState.length > 0) {
      handleSkippedQuestions(questionsInErrorState);
    } else {
      submitOrUpdate();
    }
  };

  const handleAnswerChange = useCallback((question: Question, updatedAnswer: AssessmentResultAnswer) => {
    dispatch({
      type: ActionTypeEnum.EDIT_ANSWER,
      updatedAnswer,
      question,
    });
  }, []);

  const handleCancel = useCallback(() => {
    dispatch({
      type: ActionTypeEnum.CANCEL_CHANGES,
      initialFormState: getInitialAssessmentFormState(assessment.questions, assessmentResult),
    });

    if (onCancel) {
      onCancel();
    }
  }, [assessment.questions, onCancel, assessmentResult]);

  const renderQuestions = () => assessment.questions.map((question) => (!state[question.ordinal].hidden
      && (
        <QuestionDisplay
          name={`question-${question.ordinal}`}
          question={question}
          answer={state[question.ordinal].answer}
          onAnswerChange={handleAnswerChange}
          disabled={disabled}
          error={state[question.ordinal].inErrorState}
          fieldRequiredText={fieldRequiredText}
          id={`question-${question.ordinal}`}
          key={question.uid}
          locale={locale}
        />
      )
  ));

  return (
    <Box display="block">
      <Box margin="12px">
        <Text as="h2">{assessment.contextText}</Text>
        <Text as="h3">{assessment.preQuestionText}</Text>
      </Box>
      {renderQuestions()}
      {!disabled && (
        <div>
          <Styled.Button type="submit" variant="outlined" onClick={handleCancel}>
            {cancelButtonText ?? 'Cancel'}
          </Styled.Button>
          <Styled.Button type="submit" variant="outlined" onClick={handleSubmit}>
            {submitButtonText ?? 'Submit'}
          </Styled.Button>
        </div>
      )}
    </Box>
  );
};

export default AssessmentForm;
